/**
 * BatchImageGallery - Grid display of batch-generated images
 */

import { Loader2, Download, Eye, CheckCircle2 } from 'lucide-react';
import { Button, Badge, EmptyState } from '@/components/v2/ui';
import type { BatchImage } from '@/lib/api/batch';

interface BatchImageGalleryProps {
  images: BatchImage[];
  stats: {
    total: number;
    completed: number;
    failed: number;
    pending: number;
  } | null;
  isLoading: boolean;
}

export function BatchImageGallery({ images, stats, isLoading }: BatchImageGalleryProps) {
  const completedImages = images.filter(img => img.status === 'completed' && img.output_url);
  const hasResults = completedImages.length > 0;

  const handleDownloadAll = () => {
    completedImages.forEach((img, idx) => {
      if (img.output_url) {
        const a = document.createElement('a');
        a.href = img.output_url;
        a.download = `batch-${idx + 1}.png`;
        a.click();
      }
    });
  };

  const handleDownloadSingle = (url: string, index: number) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-${index + 1}.png`;
    a.click();
  };

  return (
    <div className="space-y-4">
      {/* Stats Header */}
      {stats && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              Generierte Bilder
            </h3>
            <div className="flex gap-2">
              <Badge variant="success">{stats.completed} fertig</Badge>
              {stats.failed > 0 && (
                <Badge variant="error">{stats.failed} fehlgeschlagen</Badge>
              )}
            </div>
          </div>
          {hasResults && (
            <Button
              variant="outline"
              size="sm"
              iconLeft={<Download className="h-4 w-4" />}
              onClick={handleDownloadAll}
            >
              Alle herunterladen
            </Button>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-orange-500 animate-spin" />
        </div>
      )}

      {/* Image Grid */}
      {hasResults && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {completedImages.map((image, index) => (
            <div
              key={image.id}
              className="group relative aspect-square rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 hover:border-orange-400 dark:hover:border-orange-600 transition-all"
            >
              <img
                src={image.output_url!}
                alt={`Generated ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  iconLeft={<Eye className="h-4 w-4" />}
                  onClick={() => window.open(image.output_url!, '_blank')}
                >
                  Ansehen
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  iconLeft={<Download className="h-4 w-4" />}
                  onClick={() => handleDownloadSingle(image.output_url!, index)}
                >
                  Download
                </Button>
              </div>

              {/* Image number badge */}
              <div className="absolute top-2 left-2 px-2 py-1 rounded bg-black/70 text-white text-xs font-medium">
                #{image.sort_order + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !hasResults && (
        <EmptyState
          icon={<CheckCircle2 className="h-12 w-12 text-emerald-500" />}
          title="Batch abgeschlossen"
          description="Keine Bilder in den Ergebnissen gefunden."
        />
      )}
    </div>
  );
}
